// src/utils/actionItems.js
//
// Reading doctor action items for a clinician. The backend stores the
// triage pipeline's metadata inline in `description` (see
// ActionItemDescription.java, which parses the same formats server-side and is
// the reference for this file):
//
//   Format A (acute / high risk): "[AUTO] triage:2a4c01ca | ACUTE ALERT | HIGH | eGFR critically low — urgent renal review."
//   Format A (checklist):         "[AUTO] triage:2a4c01ca | RE-PANEL | Schedule a 90-day re-panel"
//   Format B (per marker):        "[AUTO] ORG.EGFR | IMMEDIATE | Triage:2a4c01ca | Urgent renal review"
//   Format C (legacy):            "ORG.EGFR — Urgent renal review (triage 2a4c01ca)"
//   Plain (navigator/doctor):     "LDL-C — Review statin therapy"
//
// cleanActionItemText returns the readable sentence; classifyOpenItem says
// whether it needs action now. Pure: no React, no network.

const AUTO_RE = /^(\[auto\]\s*)+/i;
const SEPARATOR = ' | ';
// "ORG.EGFR — " / "LAB.HBA1C - " : a dotted or underscored upper-case code, then a dash.
const CODE_PREFIX_RE = /^[A-Z][A-Z0-9]*(?:[._][A-Z0-9]+)+\s+[—–-]\s+/;
// "(triage 2a4c01ca)" / "(Triage:2a4c01ca)" trailing reference.
const TRIAGE_SUFFIX_RE = /\s*\(\s*triage[\s:]+[0-9a-f]{4,}\s*\)\s*$/i;

const squash = s => String(s).replace(/\s+/g, ' ').trim();

/** The human-readable clinical sentence; never contains the "[AUTO]" marker. */
export function cleanActionItemText(raw) {
  if (raw == null) return '';
  const text = String(raw).trim();
  if (!text) return '';
  if (AUTO_RE.test(text)) {
    const cut = text.lastIndexOf(SEPARATOR);
    const tail = cut >= 0 ? text.slice(cut + SEPARATOR.length) : text.replace(AUTO_RE, '');
    return squash(tail.replace(AUTO_RE, ''));
  }
  return squash(text.replace(CODE_PREFIX_RE, '').replace(TRIAGE_SUFFIX_RE, ''));
}

/** Metadata segments of an [AUTO] description ([] for anything else). */
function autoSegments(raw) {
  const text = String(raw || '').trim();
  if (!AUTO_RE.test(text)) return [];
  return text.replace(AUTO_RE, '').split(SEPARATOR).map(s => s.trim());
}

const upper = v => String(v || '').trim().toUpperCase();
const NOW_PRIORITIES = ['URGENT', 'HIGH'];

/**
 * @param {{description?: string, priority?: string, dueDate?: string, completed?: boolean}} item
 * @returns {{text: string, priority: string, acute: boolean, auto: boolean, tier: 'now'|'routine',
 *            label: string, triageId: string|null}}
 *   tier 'now' = URGENT/HIGH priority or an acute alert (Needs action now).
 */
export function classifyOpenItem(item) {
  const raw = item?.description || '';
  const segs = autoSegments(raw);
  const priority = upper(item?.priority) || 'ROUTINE';
  const tag = segs.map(upper);
  const acute = tag.includes('ACUTE ALERT') || tag.includes('IMMEDIATE');
  const highRisk = tag.includes('HIGH RISK');
  const triageSeg = segs.find(s => /^triage:/i.test(s));
  const triageId = triageSeg ? triageSeg.replace(/^triage:/i, '').trim() : null;
  const tier = acute || NOW_PRIORITIES.includes(priority) ? 'now' : 'routine';
  let label = priority.charAt(0) + priority.slice(1).toLowerCase();
  if (acute) label = 'Acute alert';
  else if (highRisk) label = 'High risk';
  return { text: cleanActionItemText(raw), priority, acute, auto: segs.length > 0, tier, label, triageId };
}

const RANK = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3, ROUTINE: 4 };

/** Open items only, split into "now" and "routine", most severe then soonest-due first. */
export function groupOpenItems(items) {
  const open = (Array.isArray(items) ? items : []).filter(i => i && !i.completed);
  const rows = open.map(i => ({ item: i, ...classifyOpenItem(i) }));
  const order = (a, b) => (Number(b.acute) - Number(a.acute))
    || ((RANK[a.priority] ?? 5) - (RANK[b.priority] ?? 5))
    || String(a.item.dueDate || '9999').localeCompare(String(b.item.dueDate || '9999'));
  return {
    now: rows.filter(r => r.tier === 'now').sort(order),
    routine: rows.filter(r => r.tier === 'routine').sort(order),
  };
}

/** What the "Needs attention" tile shows. */
export function attentionSummary(items, latestReport) {
  const { now, routine } = groupOpenItems(items);
  const score = latestReport?.consensusRiskScore;
  const level = String(latestReport?.consensusRiskLevel || '').toUpperCase();
  return {
    urgentCount: now.length,
    routineCount: routine.length,
    riskScore: score == null ? null : score,
    riskLevel: level || null,
    any: now.length > 0,
  };
}
